import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Smartphone, FileText, CheckCircle2, AlertCircle,
  Loader2, ArrowRight, ArrowLeft, RotateCcw, ExternalLink, Fingerprint,
} from "lucide-react";

/**
 * Customer KYC — PAN-only via the hitch.zone parent system (Phase 7.30c).
 *
 * Replaces the prior local-OTP + PAN+Aadhaar form. The new flow is:
 *   1. Partner enters customer mobile.
 *   2. We check the parent system's current KYC verification %:
 *      • If already verified above threshold → mark "done" immediately.
 *      • Otherwise send a hitch.zone OTP, partner enters it.
 *   3. Partner enters PAN. Server hits hitch.zone /v4/kyc and stores the
 *      returned name/DOB as customer_kyc.status=verified.
 *
 * Aadhaar is no longer collected from the partner — the parent system owns it.
 */

const STEPS = [
  { id: "mobile", label: "Mobile", icon: Smartphone },
  { id: "otp", label: "Parent OTP", icon: ShieldCheck },
  { id: "pan", label: "PAN verify", icon: FileText },
  { id: "aadhar", label: "Aadhaar", icon: Fingerprint },
  { id: "done", label: "Verified", icon: CheckCircle2 },
];

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const idx = STEPS.findIndex((x) => x.id === step);
        const active = i === idx;
        const done = i < idx;
        return (
          <div key={s.id} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border transition-colors ${
              active ? "bg-primary text-primary-foreground border-primary" :
              done ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
              "bg-muted/40 border-border text-muted-foreground"
            }`} data-testid={`step-${s.id}`}>
              <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
              <span className="font-medium">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
          </div>
        );
      })}
    </div>
  );
}


export default function CustomerKycWizard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const presetMobile = searchParams.get("mobile") || "";
  const returnTo = searchParams.get("return") || "";

  const [mobile, setMobile] = useState(presetMobile);
  const [step, setStep] = useState("mobile");
  const [statusInfo, setStatusInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const [otp, setOtp] = useState("");
  const [pan, setPan] = useState("");
  const [verifiedKyc, setVerifiedKyc] = useState(null);
  const [aadharUrl, setAadharUrl] = useState("");
  const [aadharLoading, setAadharLoading] = useState(false);
  const [verificationPct, setVerificationPct] = useState(0);

  // Auto-load status if mobile preset
  useEffect(() => {
    if (presetMobile && /^\d{10}$/.test(presetMobile)) {
      checkStatus(presetMobile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStatus = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customer-kyc/status/${m}`);
      setStatusInfo(data);
      setVerificationPct(Number(data?.verification_percentage || 0));
      if (data.effective_status === "verified" && Number(data?.verification_percentage || 0) >= 100) {
        setVerifiedKyc(data);
        setStep("done");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not check status");
    } finally { setLoading(false); }
  };

  // Step 1: mobile → check status, then send hitch OTP (if not already verified)
  const startKyc = async () => {
    if (!/^\d{10}$/.test(mobile)) {
      toast.error("Enter a valid 10-digit mobile");
      return;
    }
    setLoading(true);
    try {
      await checkStatus(mobile);
      // If already fully verified, checkStatus moved to "done".
      // Otherwise request a hitch.zone OTP and advance.
      const { data: s } = await api.get(`/customer-kyc/status/${mobile}`);
      setVerificationPct(Number(s?.verification_percentage || 0));
      if (s.effective_status === "verified" && Number(s?.verification_percentage || 0) >= 100) return;
      await api.post("/customer/hitch-otp/send", { customer_mobile: mobile });
      toast.success("OTP sent — check the customer's phone");
      setStep("otp");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start KYC");
    } finally { setLoading(false); }
  };

  // Step 2: verify hitch.zone OTP → if customer is already 100% verified
  // upstream we land on "done"; else if PAN known & auto-approved (≥ threshold
  // but <100%) we jump to the Aadhaar step; otherwise advance to "pan".
  const verifyOtp = async () => {
    if (!/^\d{4,6}$/.test(otp)) {
      toast.error("Enter a valid OTP");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/customer/hitch-otp/verify", {
        customer_mobile: mobile, otp,
      });
      const pkyc = data?.parent_kyc;
      const pct = Number(pkyc?.verification_percentage || 0);
      setVerificationPct(pct);
      if (pkyc?.auto_approved && pkyc.pan_number) {
        await checkStatus(mobile);
        if (pct >= 100) {
          setStep("done");
          toast.success(`KYC verified via parent system (${pct}%)`);
        } else {
          setStep("aadhar");
          toast.success(`PAN already verified (${pct}%). Complete Aadhaar to reach 100%.`);
        }
        return;
      }
      setStep("pan");
      toast.success("Customer authenticated. Enter PAN to complete KYC.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid OTP");
    } finally { setLoading(false); }
  };

  // Step 3: PAN → parent /v4/kyc → KYC verified (move to Aadhaar if <100%)
  const submitPan = async () => {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan.toUpperCase())) {
      toast.error("Invalid PAN (e.g. ABCDE1234F)");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/customer-kyc/pan-verify", {
        mobile, pan_number: pan.toUpperCase(),
      });
      setVerifiedKyc(data.customer_kyc);
      const pct = Number(data.customer_kyc?.verification_percentage || 0);
      setVerificationPct(pct);
      if (pct >= 100) {
        setStep("done");
        toast.success(data.skipped
          ? "Customer already verified upstream"
          : `KYC verified — ${data.customer_kyc.full_name}`);
      } else {
        setStep("aadhar");
        toast.success(`PAN verified (${pct}%). Complete Aadhaar via DigiLocker to reach 100%.`);
      }
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      if (code === "hitch_session_expired") {
        toast.error("Parent-system session expired. Re-send OTP.");
        setStep("otp");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "PAN verification failed");
      }
    } finally { setLoading(false); }
  };

  // Step 4: Aadhaar via DigiLocker — fetch URL & open in new tab.
  const fetchAadharUrl = async () => {
    setAadharLoading(true);
    try {
      const { data } = await api.get(`/customer-kyc/aadhar-verify-url/${mobile}`);
      if (!data.needs_aadhar) {
        await refreshAadharStatus({ silent: true });
        return;
      }
      setAadharUrl(data.url);
      window.open(data.url, "_blank", "noopener,noreferrer");
      toast.success("DigiLocker opened in a new tab. Complete it on the customer's device.");
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      if (code === "hitch_session_expired") {
        toast.error("Parent-system session expired. Re-send OTP.");
        setStep("otp");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not fetch DigiLocker URL");
      }
    } finally { setAadharLoading(false); }
  };

  // Re-pull verification % from hitch.zone. Used by the manual button + 15-min poll.
  const refreshAadharStatus = async ({ silent = false } = {}) => {
    if (!/^\d{10}$/.test(mobile)) return;
    if (!silent) setAadharLoading(true);
    try {
      const { data } = await api.post(`/customer-kyc/refresh-status/${mobile}`);
      const pct = Number(data?.verification_percentage || 0);
      setVerificationPct(pct);
      if (data.customer_kyc) setVerifiedKyc(data.customer_kyc);
      if (pct >= 100) {
        setStep("done");
        toast.success("Aadhaar verified — customer is now 100% KYC complete.");
      } else if (!silent) {
        toast.message(`Still at ${pct}%. Ask the customer to complete DigiLocker.`);
      }
    } catch (err) {
      if (!silent) toast.error(formatApiError(err.response?.data?.detail) || "Could not refresh status");
    } finally { if (!silent) setAadharLoading(false); }
  };

  // Auto-poll every 15 mins while on the Aadhaar step.
  useEffect(() => {
    if (step !== "aadhar") return;
    const id = setInterval(() => { refreshAadharStatus({ silent: true }); }, 15 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, mobile]);

  const reset = () => {
    setMobile(""); setOtp(""); setPan(""); setVerifiedKyc(null);
    setStatusInfo(null); setStep("mobile"); setAadharUrl(""); setVerificationPct(0);
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6 py-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Customer KYC</h1>
            <p className="text-sm text-muted-foreground">PAN-only verification via the parent system.</p>
          </div>
        </div>

        <StepIndicator step={step} />

        {/* Status snapshot — visible from any step after we've checked */}
        {statusInfo && step !== "mobile" && (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs flex items-center justify-between gap-2" data-testid="status-banner">
            <div>
              <span className="font-mono">+91 {mobile}</span> · current status:&nbsp;
              <Badge variant={statusInfo.effective_status === "verified" ? "default" : "outline"}>
                {statusInfo.effective_status || "not_started"}
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={reset}><RotateCcw className="h-3 w-3 mr-1" /> Reset</Button>
          </div>
        )}

        {step === "mobile" && (
          <div className="rounded-lg border border-border p-6 space-y-4 bg-card" data-testid="step-mobile-form">
            <Label>Customer mobile</Label>
            <Input
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit mobile"
              maxLength={10}
              className="font-mono"
              data-testid="mobile-input"
            />
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              We'll check the parent system's KYC status. If the customer is already verified upstream, we skip the OTP entirely.
            </p>
            <Button onClick={startKyc} disabled={loading || mobile.length !== 10} className="w-full" data-testid="start-kyc-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1" />}
              Continue
            </Button>
          </div>
        )}

        {step === "otp" && (
          <div className="rounded-lg border border-border p-6 space-y-4 bg-card" data-testid="step-otp-form">
            <Label>Parent-system OTP</Label>
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              maxLength={6}
              className="font-mono text-center text-lg"
              data-testid="otp-input"
              placeholder="••••••"
            />
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              Hitch.zone OTPs expire in about a minute — enter it quickly.
            </p>
            <Button onClick={verifyOtp} disabled={loading || otp.length < 4} className="w-full" data-testid="verify-otp-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Verify OTP
            </Button>
          </div>
        )}

        {step === "pan" && (
          <div className="rounded-lg border border-border p-6 space-y-4 bg-card" data-testid="step-pan-form">
            <Label>Customer PAN</Label>
            <Input
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase().slice(0, 10))}
              placeholder="ABCDE1234F"
              maxLength={10}
              className="font-mono uppercase"
              data-testid="pan-input"
            />
            <p className="text-xs text-muted-foreground">
              The parent system will verify the PAN against the income-tax database and return the registered name + DOB. No Aadhaar required.
            </p>
            <Button onClick={submitPan} disabled={loading || pan.length !== 10} className="w-full" data-testid="submit-pan-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
              Verify PAN & complete KYC
            </Button>
          </div>
        )}

        {step === "aadhar" && (
          <div className="rounded-lg border border-border p-6 space-y-4 bg-card" data-testid="step-aadhar-form">
            <div className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-primary" />
              <div>
                <div className="font-semibold">Aadhaar via DigiLocker</div>
                <div className="text-xs text-muted-foreground">
                  Verification stands at <span className="font-mono">{verificationPct}%</span>. Complete Aadhaar to reach 100%.
                </div>
              </div>
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs flex items-start gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-700" />
              <div className="text-amber-900">
                The link opens on the parent system's secure DigiLocker page. Open it on the customer's device — once they finish, this page auto-refreshes every 15 minutes, or tap "Refresh status" below to check immediately.
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={fetchAadharUrl} disabled={aadharLoading} className="flex-1" data-testid="open-digilocker-btn">
                {aadharLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1" />}
                {aadharUrl ? "Re-open DigiLocker" : "Open DigiLocker"}
              </Button>
              <Button variant="outline" onClick={() => refreshAadharStatus()} disabled={aadharLoading} className="flex-1" data-testid="refresh-status-btn">
                {aadharLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                Refresh status
              </Button>
            </div>

            {aadharUrl && (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs break-all" data-testid="aadhar-url-display">
                <span className="text-muted-foreground">DigiLocker URL:</span>{" "}
                <a href={aadharUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline font-mono">
                  {aadharUrl}
                </a>
              </div>
            )}

            <Button variant="ghost" size="sm" onClick={() => setStep("done")} data-testid="skip-aadhar-btn">
              Skip for now (continue at {verificationPct}%)
            </Button>
          </div>
        )}

        {step === "done" && verifiedKyc && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 space-y-3" data-testid="step-done">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-semibold">KYC verified</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Name</span><div className="font-semibold">{verifiedKyc.full_name || "—"}</div></div>
              <div><span className="text-muted-foreground">PAN</span><div className="font-mono">{verifiedKyc.pan_number || "—"}</div></div>
              <div><span className="text-muted-foreground">DOB</span><div className="font-mono">{verifiedKyc.dob || "—"}</div></div>
              <div><span className="text-muted-foreground">Source</span><div className="font-mono">{verifiedKyc.source || "—"}</div></div>
            </div>
            <div className="flex gap-2 pt-2">
              {returnTo && (
                <Button onClick={() => navigate(returnTo)} data-testid="return-btn">
                  Continue
                </Button>
              )}
              <Button variant="outline" onClick={reset} data-testid="verify-another-btn">
                Verify another
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
